import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Table,
  Row,
  Col,
  Space,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { ScheduleOfValues } from 'ontraccr-common';

import BorderlessButton from '../../../common/buttons/BorderlessButton';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import TableDeleteButton from './TableDeleteButton';

import DisplayText from '../../../common/text/DisplayText';
import { 
  currencyParser,
  currencyFormatter,
  percentParser,
 } from '../../../helpers/inputParsers';
import { generateId, getHasColumnPermissions, getLinkId } from '../../formHelpers';
import Permissions from '../../../auth/Permissions';
import { getIdMap } from '../../../helpers/helpers';
import useCheckTableMaxRows from '../../../common/hooks/useCheckTableMaxRows';
import {
  createHandleReloadPreload,
  getCalculationTableColumn,
  getCustomTableColumn,
  LINE_NUMBER_COLUMN,
} from './formFieldsHelpers';
import colors from '../../../constants/Colors';
import ProjectsService from '../../../projects/state/projects.service';
import {
  getSovRowClassName,
  getSovTreeColumns,
} from '../../../projects/ProjectScheduleOfValues/helpers';
import useSovExpandedRowKeys from '../../../projects/ProjectScheduleOfValues/useSovExpandedRowKeys';

const READ_ONLY_SOV_FIELDS = new Set([
  'totalChanges',
  'progressToDate',
  'previousBillings',
  'previouslyBilledQuantity',
  'quantityToDate',
  'quantityRemaining',
]);

// Rollup parents show these columns read-only; description and notes stay editable.
const PARENT_READ_ONLY_SOV_COLUMNS = new Set(ScheduleOfValues.SOV_PARENT_READ_ONLY_FIELDS);

// antd hands nested rows a sibling relative index, so sub-items need their own label.
const SOV_LINE_NUMBER_COLUMN = {
  ...LINE_NUMBER_COLUMN,
  render: (_text, record, index) => record?.lineNumber ?? index + 1,
};

// Manual rows only have a client side id, preloaded rows carry both.
const getSovTableRowKey = (record) => record?.id ?? record?.rowId;

const DEFAULT_SCHEDULE_OF_VALUES_COLUMN_LABELS = {
  number: 'Number',
  description: 'Description',
  contractAmount: 'Contract Amount',
  percentageComplete: '% Complete',
  totalChanges: 'Total Changes',
  progressToDate: 'Progress To Date',
  previousBillings: 'Previous Billings',
  invoice: 'Invoice',
  invoiceAmount: 'Invoice',
  notes: 'Notes',
  unitType: 'Unit Type',
  estimatedQuantity: 'Estimated Quantity',
  previouslyBilledQuantity: 'Previously Billed Quantity',
  billableQuantity: 'Billable Quantity',
  completedQuantity: 'Completed Quantity',
  quantityToDate: 'Quantity To Date',
  quantityRemaining: 'Quantity Remaining',
  billingCode: 'Billing Code',
};

const defaultTextRender = ({
  onValueChanged,
  key,
  isDisplay,
  textarea,
}) => function render(val, record) {
  if (isDisplay) return val;
  return (
    <OnTraccrTextInput
      textarea={textarea}
      value={val}
      onChange={(e) => {
        const {
          target: {
            value,
          } = {},
        } = e;
        onValueChanged(record.id, { [key]: value });
      }}
    />
  );
};

const defaultQuantityRender = ({
  onValueChanged,
  key,
  isDisplay,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  step = 0.01,
  precision = 2,
}) => function render(val, record) {
  if (isDisplay) {
    if (!val) return '';
    return val;
  }
  return (
    <OnTraccrNumberInput
      key={record.id}
      min={min}
      max={max}
      onChange={(value) => onValueChanged(record.id, { [key]: value })}
      value={val}
      step={step}
      precision={precision}
    />
  );
};

const defaultCurrencyRender = ({
  onValueChanged,
  key,
  isDisplay,
  parser = currencyParser,
  formatter = currencyFormatter,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  step = 0.01,
  precision = 2,
}) => function render(val, record) {
  if (isDisplay) {
    if (!val) return '$0';
    return formatter(val);
  }
  return (
    <OnTraccrNumberInput
      key={record.id}
      min={min}
      max={max}
      onChange={(value) => onValueChanged(record.id, { [key]: value })}
      value={val}
      formatter={formatter}
      parser={parser}
      step={step}
      precision={precision}
    />
  );
};

const sovPercentFormatter = (value) => (
  value || value === 0 ? `${value}%` : ''
);

const defaultPercentageRender = ({
  onValueChanged,
  key,
  isDisplay,
}) => function render(val, record) {
  const displayValue = (val || val === 0) ? Number(val) * 100 : val;
  if (isDisplay) {
    if (!displayValue && displayValue !== 0) return '';
    return sovPercentFormatter(displayValue);
  }
  return (
    <OnTraccrNumberInput
      key={record.id}
      min={-100}
      max={100}
      onChange={(value) => {
        const parsedValue = percentParser(String(value ?? ''));
        onValueChanged(record.id, {
          [key]: parsedValue === '' ? '' : Number(parsedValue) / 100,
        });
      }}
      value={displayValue}
      formatter={sovPercentFormatter}
      parser={percentParser}
    />
  );
};

const getScheduleOfValueColumnMap = ({
  onValueChanged,
  isDisplay,
}) => {
  const invoiceAmountRenderer = defaultCurrencyRender({
    onValueChanged,
    key: 'invoiceAmount',
    isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('invoiceAmount'),
  });
  
  return {
    number: {
      dataIndex: 'number',
      width: 100,
      render: defaultTextRender({ onValueChanged, 
        key: 'number', 
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('number'),
      }),
    },
    description: {
      dataIndex: 'description',
      width: 100,
      render: defaultTextRender({ onValueChanged, 
        key: 'description', 
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('description'),
      }),
    },
    contractAmount: {
      dataIndex: 'contractAmount',
      width: 100,
      render: defaultCurrencyRender({ onValueChanged, 
        key: 'contractAmount', 
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('contractAmount'),
      }),
    },
    percentageComplete: {
      dataIndex: 'percentageComplete',
      width: 100,
      render: defaultPercentageRender({
        onValueChanged,
        key: 'percentageComplete',
        isDisplay,
      }),
    },
    totalChanges: {
      dataIndex: 'totalChanges',
      width: 100,
      render: defaultCurrencyRender({ 
        onValueChanged, 
        key: 'totalChanges', 
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('totalChanges'),
      }),
    },
    progressToDate: {
      dataIndex: 'progressToDate',
      width: 100,
      render: defaultCurrencyRender({ 
        onValueChanged, 
        key: 'progressToDate', 
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('progressToDate'),
      }),
    },
    previousBillings: {
      dataIndex: 'previousBillings',
      width: 100,
      render: defaultCurrencyRender({ 
        onValueChanged, 
        key: 'previousBillings', 
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('previousBillings'),
      }),
    },
    invoice: {
      dataIndex: 'invoiceAmount',
      width: 100,
      // for backwards compatibility with old data
      render: (val, record) => invoiceAmountRenderer(val ?? record?.invoice, record),
    },
    notes: {
      dataIndex: 'notes',
      width: 100,
      render: defaultTextRender({ 
        onValueChanged, 
        key: 'notes', 
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('notes'),
      }),
    },
    unitType: {
      dataIndex: 'unitType',
      width: 100,
      render: defaultTextRender({ 
        onValueChanged, 
        key: 'unitType', 
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('unitType'),
      }),
    },
    estimatedQuantity: {
      dataIndex: 'estimatedQuantity',
      width: 100,
      render: defaultQuantityRender({
        onValueChanged,
        key: 'estimatedQuantity',
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('estimatedQuantity'),
      }),
    },
    previouslyBilledQuantity: {
      dataIndex: 'previouslyBilledQuantity',
      width: 100,
      render: defaultQuantityRender({
        onValueChanged,
        key: 'previouslyBilledQuantity',
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('previouslyBilledQuantity'),
      }),
    },
    billableQuantity: {
      dataIndex: 'billableQuantity',
      width: 100,
      render: defaultQuantityRender({
        onValueChanged,
        key: 'billableQuantity',
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('billableQuantity'),
      }),
    },
    completedQuantity: {
      dataIndex: 'completedQuantity',
      width: 140,
      render: defaultQuantityRender({
        onValueChanged,
        key: 'completedQuantity',
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('completedQuantity'),
        min: Number.MIN_SAFE_INTEGER,
      }),
    },
    quantityToDate: {
      dataIndex: 'quantityToDate',
      width: 160,
      render: defaultQuantityRender({
        onValueChanged,
        key: 'quantityToDate',
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('quantityToDate'),
        min: Number.MIN_SAFE_INTEGER,
      }),
    },
    quantityRemaining: {
      dataIndex: 'quantityRemaining',
      width: 140,
      render: (_val, record) => {
        const remaining = ScheduleOfValues.getQuantityRemaining(record);
        if (remaining < 0) {
          return <span style={{ color: colors.ONTRACCR_RED }}>{remaining}</span>;
        }
        return remaining;
      },
    },
    billingCode: {
      dataIndex: 'billingCode',
      width: 100,
      render: defaultTextRender({ 
        onValueChanged, 
        key: 'billingCode', 
        isDisplay: isDisplay || READ_ONLY_SOV_FIELDS.has('billingCode'),
      }),
    },
  };
};

const scheduleOfValueColumns = ({
  onDelete,
  onValueChanged,
  onAddSubItem,
  columns,
  isDisplay,
  requiredColumns,
  columnPermissions,
  userPositionId,
  responses,
}) => {
  const cols = [SOV_LINE_NUMBER_COLUMN];
  const colMap = getScheduleOfValueColumnMap({
    onValueChanged,
    isDisplay,
  });
  const displayColMap = isDisplay
    ? colMap
    : getScheduleOfValueColumnMap({ onValueChanged, isDisplay: true });

  // Rollup parents show derived numbers, but keep description and notes editable.
  const getRollupAwareColumn = (key) => {
    const col = colMap[key];
    if (isDisplay || !PARENT_READ_ONLY_SOV_COLUMNS.has(key)) return col;
    const displayRender = displayColMap[key].render;
    return {
      ...col,
      render: (val, record, index) => (
        record?.hasSubItems
          ? displayRender(val, record, index)
          : col.render(val, record, index)
      ),
    };
  };

  columns.forEach((col) => {
    if (col.key in colMap) {
      const headerText = col.name || DEFAULT_SCHEDULE_OF_VALUES_COLUMN_LABELS[col.key] || col.key;
      cols.push({
        ...getRollupAwareColumn(col.key),
        title: (
          <div className={requiredColumns && 'form-required-field'}>
            {headerText}
          </div>
        ),
      });
    } else if (col.isCustomText) {
      if (!getHasColumnPermissions({
        columnPermissions,
        column: col,
        userPositionId,
        userId: Permissions.id,
      })) {
        return;
      }
      cols.push(getCustomTableColumn(col, {
        width: 100,
        isDisplay,
        requiredColumns,
        responses,
        onChange: (record, value) => onValueChanged(record.id, { [col.key]: value }),
      }));
    } else if (col.isCalculation) {
      cols.push(getCalculationTableColumn(col, {
        width: 100,
        isDisplay,
      }));
    }
  });

  if (!isDisplay && onAddSubItem) {
    cols.push({
      title: 'Sub-Item',
      dataIndex: 'addSubItem',
      width: 60,
      align: 'center',
      render: (_, record) => (
        // One level only, and only under the row types the backend accepts as parents.
        ScheduleOfValues.canRowHaveSubItems(record) ? (
          <BorderlessButton
            iconNode={<PlusOutlined />}
            onClick={() => onAddSubItem(record)}
          />
        ) : null
      ),
    });

    cols.push({
      title: '',
      dataIndex: '',
      width: 100,
      render: (_, record) => (
        <TableDeleteButton onConfirm={() => onDelete(record.id)} />
      ),
    });
  }

  return cols;
};

function ScheduleOfValuesTablePreview({
  title = null,
  columns = [],
  previewProps = {},
  setPreviewProps,
  isDisplay,
  id,
  responses = {},
  setResponses,
  responding = false,
  configProps = {},
  showCondensedView,
}) {
  const {
    hideAddNewButton,
    requiredColumns,
    preventEdits,
    preloadExistingEntries,
    preloadProject,
    preloadDropdown,
    preloadSovTemplateId,
    preloadSovSectionId,
    columnPermissions = {},
  } = configProps ?? {};

  const positionNames = useSelector((state) => state.settings.positionNames);
  const users = useSelector((state) => state.users.users);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);
  
  const [loadingPreload, setLoadingPreload] = useState(false);
  const values = previewProps.values || [];
  const {
    selected: previewSelected = [],
  } = previewProps;
  const {
    [id]: {
      values: responseSelected = [],
    } = {},
  } = responses;

  const selected = responding ? responseSelected : previewSelected;
  const linkedProjectId = useMemo(() => (
    getLinkId({ responses, defaultValue: null, linkField: preloadDropdown })
  ), [responses, preloadDropdown]);
  const preloadProjectId = preloadProject ?? linkedProjectId;

  const defaultScheduleOfValue = {
    number: '',
    description: '',
    contractAmount: 0,
    totalChanges: 0,
    progressToDate: 0,
    previousBillings: 0,
    invoiceAmount: 0,
    notes: '',
    unitType: '',
    estimatedQuantity: 0,
    previouslyBilledQuantity: 0,
    billableQuantity: 0,
    completedQuantity: 0,
    quantityToDate: 0,
    billingCode: '',
  };

  const updateResponses = useCallback((newData = {}) => {
    setResponses((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id]),
        dataType: 'ScheduleOfValues',
        ...newData,
      },
    }));
  }, [id, setResponses]);

  // The stored response is a flat ledger, so a parent's numbers only stay equal
  // to the sum of its sub-items if they are recomputed on every write.
  const setSovRows = useCallback((newRows) => {
    const syncedRows = ScheduleOfValues.syncSovParentRows(newRows);
    if (responding) {
      updateResponses({ values: syncedRows, columns });
    } else {
      setPreviewProps({ ...previewProps, selected: syncedRows });
    }
  }, [previewProps, setPreviewProps, responding, columns, updateResponses]);

  const onAddNewClicked = useCallback(() => {
    const newEntry = {
      id: generateId(),
      ...defaultScheduleOfValue,
    };
    setSovRows(selected.concat([newEntry]));
  }, [selected, setSovRows]);

  const onAddSubItem = useCallback((parent) => {
    const parentRowId = ScheduleOfValues.getSovRowId(parent);
    const newEntry = {
      id: generateId(),
      ...defaultScheduleOfValue,
      parentRowId,
      sectionId: parent.sectionId ?? null,
      projectId: parent.projectId ?? null,
      isChangeOrder: false,
    };

    // The first sub-item takes over the parent's numbers so totals do not change.
    if (!parent.children?.length) {
      ScheduleOfValues.SOV_ROLLUP_FIELDS.forEach((field) => {
        newEntry[field] = parent[field] ?? 0;
      });
      // Older drafts key the invoice column as "invoice".
      newEntry.invoiceAmount = parent.invoiceAmount ?? parent.invoice ?? 0;
      newEntry.percentageComplete = parent.percentageComplete ?? 0;
      newEntry.unitType = parent.unitType ?? '';
      newEntry.billingCode = parent.billingCode ?? '';
    }

    setSovRows(selected.concat([newEntry]));
  }, [selected, setSovRows]);

  const onDelete = useCallback((deletedId) => {
    const deletedRow = selected.find((item) => item.id === deletedId);
    // Deleting a parent takes its sub-items with it.
    const deletedIds = new Set([deletedId, deletedRow?.rowId].filter((rowId) => rowId));
    setSovRows(selected.filter((item) => (
      !deletedIds.has(item.id)
      && !(ScheduleOfValues.isSubItem(item) && deletedIds.has(item.parentRowId))
    )));
  }, [selected, setSovRows]);

  const onReloadPreload = useCallback(async (forceReload = false) => {
    if (!responding || !preloadExistingEntries) return;
    if (!preloadProjectId || !preloadSovSectionId) {
      if (forceReload) updateResponses({ values: [], columns });
      return;
    }
    setLoadingPreload(true);
    try {
      const { data: preloadedRows = [] } = await ProjectsService.getScheduleOfValuesPreloadRows({
        projectId: preloadProjectId,
        sectionId: preloadSovSectionId,
        templateId: preloadSovTemplateId,
      });
      const manualRows = forceReload ? [] : selected.filter((item) => !item.isPreloaded);
      const knownIds = new Set(
        preloadedRows.concat(manualRows).map(ScheduleOfValues.getSovRowId),
      );
      // Manual sub-items whose parent is gone after the reload cannot be shown.
      const keptManualRows = manualRows.filter((item) => (
        !ScheduleOfValues.isSubItem(item) || knownIds.has(item.parentRowId)
      ));
      setSovRows(preloadedRows.concat(keptManualRows));
    } finally {
      setLoadingPreload(false);
    }
  }, [
    responding,
    preloadExistingEntries,
    preloadProjectId,
    preloadSovSectionId,
    preloadSovTemplateId,
    selected,
    updateResponses,
    setSovRows,
    columns,
  ]);

  useEffect(() => {
    onReloadPreload();
  }, [
    responding,
    preloadExistingEntries,
    preloadProjectId,
    preloadSovSectionId,
    preloadSovTemplateId,
  ]);

  const onValueChanged = useCallback((changedId, newData = {}) => {
    setSovRows([...selected].map((item) => {
      if (item.id !== changedId) return item;
      return ScheduleOfValues.getUpdatedRow({
        row: item,
        payload: newData,
      });
    }));
  }, [selected, setSovRows]);

  const handleReloadPreload = useCallback(
    createHandleReloadPreload({
      responses,
      id,
      onReload: () => onReloadPreload(true),
    }),
    [responses, id, onReloadPreload],
  );

  const tableColumns = useMemo(() => (
    scheduleOfValueColumns({
      onDelete,
      onValueChanged,
      // Hiding Add Row means the row set is fixed, so sub-items cannot be added either.
      onAddSubItem: hideAddNewButton ? null : onAddSubItem,
      columns,
      isDisplay: isDisplay || preventEdits,
      requiredColumns,
      columnPermissions,
      userPositionId,
      responses,
    })
  ), [
    columns,
    isDisplay,
    onDelete,
    onValueChanged,
    onAddSubItem,
    hideAddNewButton,
    requiredColumns,
    preventEdits,
    columnPermissions,
    userPositionId,
    responses,
  ]);

  // Responses stay a flat ledger; the tree is only how the table is drawn.
  const flatRows = useMemo(() => (
    isDisplay && !responding ? values : selected
  ), [isDisplay, responding, values, selected]);

  const dataSource = useMemo(() => ScheduleOfValues.buildSovTree(flatRows), [flatRows]);

  const parentRowKeys = useMemo(() => (
    dataSource.filter((row) => row.hasSubItems).map(getSovTableRowKey)
  ), [dataSource]);

  const treeExpandable = useSovExpandedRowKeys(parentRowKeys);

  // The line number column is fixed left, so the expand column has to be too.
  const treeColumns = useMemo(
    () => getSovTreeColumns(tableColumns, dataSource, { fixed: 'left' }),
    [tableColumns, dataSource],
  );

  const rowClassName = useMemo(() => getSovRowClassName(dataSource), [dataSource]);

  const {
    shouldAddButtonBeEnabled = true,
  } = useCheckTableMaxRows({
    configProps,
    currentRowsLength: flatRows?.length,
  });

  const showReloadButton = preloadExistingEntries && responding && !isDisplay && !preventEdits;

  return (
    <Row style={{ marginTop: showCondensedView ? 0 : 15 }}>
      <Row justify="space-between" style={{ width: '100%', marginBottom: 10 }}>
        <Col>
          {title}
        </Col>
        {!isDisplay && (
          <Space gutter={10}>
            {showReloadButton && (
              <OnTraccrButton
                title="Reload Preloaded Data"
                onClick={handleReloadPreload}
                icon={<ReloadOutlined spin={loadingPreload} />}
              />
            )}
            {!hideAddNewButton && (
              <OnTraccrButton
                title="Add Row"
                onClick={onAddNewClicked}
                disabled={!shouldAddButtonBeEnabled}
              />
            )}
          </Space>
        )}
      </Row>
      { !showCondensedView || flatRows?.length ? (
        <Table
          className='form-table-preview'
          style={{ width: '100%', overflow: 'auto' }}
          columns={treeColumns}
          size="small"
          pagination={false}
          dataSource={dataSource}
          rowKey={getSovTableRowKey}
          rowClassName={rowClassName}
          expandable={treeExpandable}
          // Fixed columns only get real sticky offsets when the table scrolls itself.
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <DisplayText title="No Schedule of Values Selected" style={{ marginBottom: 0 }} />
      )}
    </Row>
  );
}

ScheduleOfValuesTablePreview.propTypes = {
  title: PropTypes.node,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
  })),
  previewProps: PropTypes.shape({
    values: PropTypes.arrayOf(PropTypes.shape({})),
    selected: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  setPreviewProps: PropTypes.func,
  isDisplay: PropTypes.bool,
  id: PropTypes.string,
  responses: PropTypes.object,
  setResponses: PropTypes.func,
  responding: PropTypes.bool,
  configProps: PropTypes.object,
  showCondensedView: PropTypes.bool,
};

ScheduleOfValuesTablePreview.defaultProps = {
  title: null,
  columns: [],
  previewProps: {},
  responses: {},
  isDisplay: false,
  responding: false,
  configProps: {},
  showCondensedView: false,
};

export default ScheduleOfValuesTablePreview;